/** SubstituteRoll (Foundry: rule-element/substitute-roll.ts): a fixed d20 result (Assurance) for a selector's checks. */
import { isValidPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggify } from "../slug";
import type { EngineState } from "../state";
import type { RuleHandler, RuleInstance } from "./base";

interface SubstituteRollData {
  selectors: string[];
  value: unknown;
  required: boolean;
  effectType: "fortune" | "misfortune";
  removeAfterRoll: unknown;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const raw = s.selector ?? s.selectors ?? "check";
  const selectors = (Array.isArray(raw) ? raw : [raw]).filter((x): x is string => typeof x === "string" && x.length > 0);
  if (selectors.length === 0) return rule.fail("selector: must be a non-empty string");
  const value = cleanRuleValue(s.value);
  if (value === undefined) return rule.fail("value: may not be undefined");
  if (s.effectType !== undefined && s.effectType !== "fortune" && s.effectType !== "misfortune") return rule.fail(`effectType: "${String(s.effectType)}" is not fortune or misfortune`);
  if (s.removeAfterRoll !== undefined && !(typeof s.removeAfterRoll === "boolean" || s.removeAfterRoll === "if-enabled" || isValidPredicate(s.removeAfterRoll))) return rule.fail("removeAfterRoll: must be a boolean, \"if-enabled\" or a predicate");
  if (s.removeAfterRoll && rule.item.type !== "effect") return rule.fail("removeAfterRoll: may only be used with effects");
  rule.data = { selectors, value, required: s.required === true, effectType: (s.effectType as "fortune" | "misfortune" | undefined) ?? "fortune", removeAfterRoll: s.removeAfterRoll ?? false } satisfies SubstituteRollData as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as SubstituteRollData;
  const value = Math.min(20, Math.max(1, Math.trunc(Number(resolveValue(rule, state, data.value)))));
  if (Number.isNaN(value)) return rule.fail("value must resolve to a number");
  for (const raw of data.selectors) {
    const selector = resolveInjectedProperties(rule, state, raw);
    if (rule.ignored) return;
    (state.synthetics.rollSubstitutions[selector] ??= []).push({
      slug: rule.slug ?? sluggify(rule.item.name),
      label: state.catalogue?.localize(rule.label) ?? rule.label,
      value,
      predicate: resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false }),
      parentOptions: rule.item.getRollOptions("parent"),
      required: data.required,
      selected: data.required,
      effectType: data.effectType,
      source: rule.item.uuid,
      sourceName: rule.item.name,
      removeAfterRoll: data.removeAfterRoll === true ? true : data.removeAfterRoll === "if-enabled" ? "if-enabled" : isValidPredicate(data.removeAfterRoll) ? data.removeAfterRoll : false,
    });
  }
}

export const SubstituteRollHandler: RuleHandler = { key: "SubstituteRoll", parse, beforePrepareData };
